/**
 * CommandPalette — Cmd+K global search overlay.
 * Searches across all sections: chat sessions, projects, routes, exercises, skills.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  MessageSquare, FolderGit2, Activity, Dumbbell, Cpu,
  DollarSign, Code2, Users, Plug, Bot, FileText, Shield,
  ScrollText, Bell, Settings, Search, BarChart3, CheckSquare,
} from "lucide-react";

interface PaletteItem {
  id: string;
  label: string;
  section: string;
  icon: React.ReactNode;
  action: () => void;
}

const ROUTES: { path: string; label: string; icon: React.ReactNode }[] = [
  { path: "/chat", label: "Chat", icon: <MessageSquare size={14} /> },
  { path: "/crew", label: "Crew (Agents)", icon: <Users size={14} /> },
  { path: "/mcp", label: "MCP Servers", icon: <Plug size={14} /> },
  { path: "/subagents", label: "Subagents", icon: <Bot size={14} /> },
  { path: "/projects", label: "Projects", icon: <FolderGit2 size={14} /> },
  { path: "/editor", label: "Code Editor", icon: <Code2 size={14} /> },
  { path: "/context", label: "Context", icon: <FileText size={14} /> },
  { path: "/skills", label: "Skills", icon: <Cpu size={14} /> },
  { path: "/ops", label: "Operaciones", icon: <Activity size={14} /> },
  { path: "/security", label: "Security", icon: <Shield size={14} /> },
  { path: "/logs", label: "Logs", icon: <ScrollText size={14} /> },
  { path: "/notifications", label: "Notificaciones", icon: <Bell size={14} /> },
  { path: "/gym", label: "Gym Analytics", icon: <Dumbbell size={14} /> },
  { path: "/costs", label: "Costos", icon: <DollarSign size={14} /> },
  { path: "/habits", label: "Habits", icon: <CheckSquare size={14} /> },
  { path: "/brief", label: "Daily Brief", icon: <BarChart3 size={14} /> },
  { path: "/settings", label: "Settings", icon: <Settings size={14} /> },
];

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Build items list
  const items = useMemo<PaletteItem[]>(() => {
    return ROUTES.map((r) => ({
      id: r.path,
      label: r.label,
      section: "Navegar",
      icon: r.icon,
      action: () => {
        navigate({ to: r.path as string });
        setOpen(false);
      },
    }));
  }, [navigate]);

  // Filter by query
  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.toLowerCase();
    return items.filter((item) =>
      item.label.toLowerCase().includes(q) ||
      item.id.toLowerCase().includes(q)
    );
  }, [items, query]);

  // Keyboard shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((v) => !v);
        setQuery("");
        setSelectedIndex(0);
      }
      if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open]);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  // Reset selection on filter change
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && filtered[selectedIndex]) {
      filtered[selectedIndex].action();
    }
  }, [filtered, selectedIndex]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-start justify-center pt-[15vh]">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} />

      {/* Palette */}
      <div
        className="relative w-full max-w-lg bg-[var(--bw-bg)] border border-[var(--bw-border)] shadow-2xl"
        style={{ boxShadow: "var(--bw-glow-accent)" }}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[var(--bw-border)]">
          <Search size={16} className="text-[var(--bw-accent)] flex-shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Buscar..."
            className="flex-1 bg-transparent text-[var(--bw-fg)] text-sm font-mono outline-none placeholder-[var(--bw-muted)]"
          />
          <kbd className="text-[10px] text-[var(--bw-muted)] border border-[var(--bw-border)] px-1.5 py-0.5 font-mono">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div className="max-h-[300px] overflow-y-auto py-1">
          {filtered.length === 0 && (
            <p className="text-sm text-[var(--bw-muted)] text-center py-4">Sin resultados</p>
          )}
          {filtered.map((item, i) => (
            <button
              key={item.id}
              onClick={item.action}
              className={`
                w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors
                ${i === selectedIndex
                  ? "bg-[var(--bw-accent)]/10 text-[var(--bw-accent)]"
                  : "text-[var(--bw-fg)] hover:bg-[var(--bw-surface-2)]"
                }
              `}
            >
              <span className="flex-shrink-0">{item.icon}</span>
              <span className="flex-1 text-left font-mono">{item.label}</span>
              <span className="text-[10px] text-[var(--bw-muted)]">{item.section}</span>
            </button>
          ))}
        </div>

        {/* Footer hint */}
        <div className="px-4 py-2 border-t border-[var(--bw-border)] flex items-center gap-4 text-[10px] text-[var(--bw-muted)]">
          <span>↑↓ navegar</span>
          <span>↵ abrir</span>
          <span>esc cerrar</span>
        </div>
      </div>
    </div>
  );
}
